import { InputHTMLAttributes, ReactNode, forwardRef, useId } from 'react'
import { cn } from '../../lib/utils'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  helpText?: string
  /** Control rendered flush to the inside-right edge of the field (e.g. a
   *  password reveal toggle). Add matching right padding via className so the
   *  value can't run underneath it. */
  endAdornment?: ReactNode
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helpText, endAdornment, className = '', type, id, ...props }, ref) => {
    // Fall back to a generated id so the label can be associated via htmlFor
    const generatedId = useId()
    const inputId = id ?? generatedId
    const inputElement = (
      <input
        type={type}
        id={inputId}
        ref={ref}
        data-slot="input"
        className={cn(
          "file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
          "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
          error && "border-destructive",
          className
        )}
        aria-invalid={error ? true : undefined}
        {...props}
      />
    )

    // The adornment is positioned against the input alone, not the labelled
    // wrapper — otherwise it would centre itself over the label + helper text
    // rather than the field.
    const controlElement = endAdornment ? (
      <div className="relative">
        {inputElement}
        <div className="absolute inset-y-0 right-0 flex items-center">{endAdornment}</div>
      </div>
    ) : (
      inputElement
    )

    // If no label, error, or helpText, return just the control
    if (!label && !error && !helpText) {
      return controlElement
    }
    
    // Otherwise, wrap with label and helper text
    return (
      <div className="w-full">
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-foreground mb-1">
            {label}
          </label>
        )}
        {controlElement}
        {error && (
          <p className="mt-1 text-xs text-destructive">{error}</p>
        )}
        {helpText && !error && (
          <p className="mt-1 text-xs text-muted-foreground">{helpText}</p>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

export default Input
export { Input }
